import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withFieldProps } from 'hooks/useFieldProps';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import { withStyles } from 'theme/withStyles';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import rootStyles from './styles/root';

const styles = ({ oaoButtons: buttons, palette, spacing, typography }) => {
  const trackStyles = {
    background: palette.grey.light,
    border: '0 solid #010101',
    borderRadius: 0,
    boxShadow: '0 0 0 rgba(0, 0, 0, 0), 0 0 0 rgba(13, 13, 13, 0)',
    cursor: 'pointer',
    height: 1,
    width: '100%',
  };

  const thumbStyles = {
    background: palette.grey.light,
    border: `4px solid ${palette.background}`,
    borderRadius: 14,
    boxShadow: `0 0 2px #000, 0 0 0 ${palette.grey.light}`,
    boxSizing: 'border-box',
    cursor: 'pointer',
    height: 28,
    width: 28,
  };

  const msFill = {
    border: '0 solid #010101',
    borderRadius: 0,
    boxShadow: 0,
  };
  return {
    '@media all and (-ms-high-contrast: none)': {
      '@global': {
        '*::-ms-backdrop, input[type=range]': {
          margin: [-19, 0, 0, 0],
        },
      },
    },
    inputWrap: {
      '& .current-value': {
        ...typography.p3,
        fontWeight: typography.fontWeights.bold,
        margin: [0, 0, 0, -48],
        position: 'absolute',
        textAlign: 'center',
        top: -19,
        width: 100,
      },
      '& .input': {
        flex: 1,
        padding: [0, 15],
        position: 'relative',
      },
      '& .max': {
        ...typography.p3,
        padding: [7, 0, 0, 7],
      },
      '& .min': {
        ...typography.p3,
        padding: [7, 7, 0, 0],
      },
      '& .track': {
        background: buttons.primary.background,
        height: 3,
        left: -13,
        position: 'absolute',
        top: 14,
      },
      '& .value-wrapper': {
        padding: '55px 0 0',
        pointerEvents: 'none',
        position: 'relative',
      },
      '& input[type=range]': {
        '&$touched': {
          '&::-moz-range-thumb': {
            ...thumbStyles,
            background: buttons.primary.background,
          },
          '&::-ms-thumb': {
            ...thumbStyles,
            background: buttons.primary.background,
          },
          '&::-webkit-slider-thumb': {
            ...thumbStyles,
            appearance: 'none',
            background: buttons.primary.background,
            marginTop: -14,
          },
        },
        '&::-moz-focus-outer': {
          border: 0,
        },
        '&::-moz-range-thumb': thumbStyles,
        '&::-moz-range-track': trackStyles,
        '&::-ms-fill-lower': {
          ...msFill,
          background: buttons.primary.background,
        },
        '&::-ms-fill-upper': {
          ...msFill,
          background: palette.grey.light,
        },
        '&::-ms-thumb': thumbStyles,
        '&::-ms-tooltip': {
          display: 'none',
        },
        '&::-ms-track': {
          background: 'transparent',
          borderColor: 'transparent',
          color: 'transparent',
          cursor: 'pointer',
          height: 20,
          width: '100%',
        },
        '&::-webkit-slider-runnable-track': {
          ...trackStyles,
          marginTop: 7,
        },
        '&::-webkit-slider-thumb': {
          ...thumbStyles,
          appearance: 'none',
          marginTop: -14,
        },
        appearance: 'none',
        background: 'none',
        left: 0,
        outline: 'none',
        position: 'absolute',
        top: 6,
        width: '100%',
      },
      display: 'flex',
      marginTop: 34,
    },
    label: {
      ...typography.p,
      ...typography.p2,
    },
    root: {
      ...rootStyles({ spacing }),
      '&.loading .spinner': {
        marginRight: -8,
        marginTop: 47,
      },

      '&.loading .wrapper': {
        opacity: 0.2,
      },
    },
    touched: {},
  };
};

const getValue = (slider, value) => {
  if (!value && value !== 0) {
    return null;
  }
  const { max, min, prefix, suffix } = slider;
  const stringVal = String(value);
  const floatVal = parseFloat(stringVal.replace(/[^0-9]/g, ''));
  const isAtMax = floatVal - min === max - min;

  return prefix + floatVal.toLocaleString() + (isAtMax ? suffix : '');
};

const AmountSlider = ({
  classes,
  error,
  fieldDomId,
  fieldOptionalLabelSuffix,
  help,
  label,
  loading,
  name,
  onChange,
  required,
  slider,
  tooltip,
  validate,
  value,
}) => {
  const labelString = `${label}${
    !required ? `<small> ${fieldOptionalLabelSuffix || '(Optional)'}</small>` : ''
  }`;
  const [localValue, setLocalValue] = useState(value);
  const handleInput = useCallback(
    (e) => {
      onChange(name, e.target.value);
      validate();
    },
    [name, onChange, validate],
  );
  return (
    <>
      <div className={classnames('form-group', { error, loading }, classes.root)}>
        <Spinner loading={loading} />
        <RawHtml
          type="label"
          className={classnames('flyup', 'touched', 'label-wrap', classes.label)}
          htmlFor={fieldDomId}
        >
          {labelString}
        </RawHtml>
        <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} handleYOffset={2} />
        <Help help={help} id={`help-${name}`} />
        <div className={classnames('wrapper', classes.inputWrap)}>
          <RawHtml className="min">{slider.minLabel}</RawHtml>
          <div className="input">
            <input
              id={fieldDomId}
              type="range"
              aria-invalid={error}
              min={slider.min}
              max={slider.max}
              step={slider.step}
              value={localValue}
              onMouseUp={handleInput}
              onTouchEnd={handleInput}
              onChange={(e) => setLocalValue(e.target.value)}
              className={classnames({
                [classes.touched]: localValue !== '',
              })}
              aria-describedby={
                classnames({
                  [`help-${name}`]: help,
                  [`error-${name}`]: error,
                }) || undefined
              }
            />
            <div className="value-wrapper">
              <span
                className="track"
                style={{ width: `${(localValue / (slider.max - slider.min)) * 100}%` }}
              />
              <div
                className="current-value"
                style={{ left: `${(localValue / (slider.max - slider.min)) * 100}%` }}
              >
                {getValue(slider, localValue)}
              </div>
            </div>
          </div>
          <RawHtml className="max">{slider.maxLabel}</RawHtml>
        </div>
      </div>

      <ErrorMsg name={name} />
    </>
  );
};

AmountSlider.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  fieldOptionalLabelSuffix: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool.isRequired,
  slider: PropTypes.shape({
    max: PropTypes.number.isRequired,
    maxLabel: PropTypes.string,
    min: PropTypes.number.isRequired,
    minLabel: PropTypes.string,
    step: PropTypes.number.isRequired,
    suffix: PropTypes.string,
  }).isRequired,
  tooltip: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const AmountSliderStyled = withStyles(styles)(AmountSlider);

const mapStateToProps = ({ fields, resources }, { name }) => ({
  fieldOptionalLabelSuffix: resources.fieldOptionalLabelSuffix ?? '',
  slider: fields[name].slider,
});

export default withFieldProps(mapStateToProps)(AmountSliderStyled);
